import React, { useState, useEffect } from 'react';
import { fetchCompanies } from '../api';
import Loader from '../components/Loader';

function CompanyRow({ company: c, rank, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'var(--surface2)' : 'transparent',
        borderBottom: '1px solid var(--border)',
        padding: '10px 4px', cursor: 'pointer',
        transition: 'background 0.15s',
        display: 'flex', alignItems: 'center', gap: 12,
      }}
    >
      {/* Logo letter */}
      <div style={{
        width: 28, height: 28, borderRadius: 6, flexShrink: 0,
        background: 'var(--surface2)', border: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700,
      }}>
        {c.name?.charAt(0) || '🏢'}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: hovered ? 'var(--blue)' : 'var(--text)', transition: 'color 0.15s' }}>
            {c.name}
          </div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginTop: 2 }}>
          {c.totalReviews} report{c.totalReviews !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Arrow */}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke={hovered ? 'var(--blue)' : 'var(--text3)'} strokeWidth="2.5"
        style={{ flexShrink: 0, marginTop: 8, transition: 'stroke 0.15s' }}>
        <path d="m9 18 6-6-6-6" />
      </svg>
    </div>
  );
}

export default function CompanyHeatMapPage({ setView, setSelectedCompany, setSelectedRole }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCompanies({ limit: 50, sortBy: 'totalReviews' })
      .then(data => setCompanies(data.companies || []))
      .catch(() => setError('Failed to load companies. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const handleClick = (c) => {
    if (setSelectedRole) setSelectedRole(null);
    if (setSelectedCompany) setSelectedCompany(c.name, 'All');
    setView('company-reviews');
  };

  return (
    <div style={{ animation: 'fadeUp 0.35s ease-out', paddingTop: 8 }}>
      {/* Header — sticky */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--bg)', padding: '2px 0 6px', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: 15, fontWeight: 900, margin: 0, color: 'var(--text)', whiteSpace: 'nowrap' }}>
            Company Heat Map
          </h1>
          {/* Search bar inline */}
          <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none' }}
              width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search company…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '6px 10px 6px 28px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--surface2)',
                fontSize: 12, color: 'var(--text)', outline: 'none',
                fontFamily: 'var(--sans)', transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--blue)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text3)', fontSize: 14, lineHeight: 1, padding: 2,
              }}>×</button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <Loader inline message="Loading company data…" />
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--red)', fontSize: 14 }}>{error}</div>
      ) : companies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)', fontSize: 14 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
          <div style={{ fontWeight: 600 }}>No company data yet</div>
        </div>
      ) : (
        <>
          {(() => {
            const filtered = search
              ? companies.filter(c => (c.name || '').toLowerCase().includes(search.toLowerCase()))
              : companies;
            return filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)', fontSize: 14 }}>
                No companies match "{search}"
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14, fontWeight: 500 }}>
                  {filtered.length} compan{filtered.length !== 1 ? 'ies' : 'y'} · click any to read interview experiences
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--border)' }}>
                  {filtered.map((c, i) => (
                    <CompanyRow key={c.id || c.slug || i} company={c} rank={i + 1} onClick={() => handleClick(c)} />
                  ))}
                </div>
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
